import { useQuery } from "@tanstack/react-query";
import { api } from "../../../lib/api.js";
import type { Guide } from "./types.js";
import { Lead, H2, P, AppLink, Callout } from "./primitives.js";

export const meta = {
  id: "credentials-reference",
  title: "Credentials reference",
  category: "Reference",
  lastUpdated: "2026-05-23",
  description: "Auto-generated from the live integration registry. Every credential the app supports, with current configured/not-configured state.",
  relatedGuides: ["getting-started"],
  readTimeMin: 3,
} as const;

const CATEGORY_LABEL: Record<string, string> = {
  llm: "AI / LLM",
  search: "Search & SERP",
  scraping: "Scraping",
  media: "Media",
  email: "Email",
  messaging: "Messaging",
  social: "Social",
  cms: "CMS",
  infra: "Infrastructure",
};

export function Body() {
  const { data, isPending } = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.listIntegrations(),
  });

  return (
    <>
      <Lead>
        Every credential the app supports, with current configuration state pulled live from the running server. Click "Setup" to jump straight to that card on the <AppLink to="credentials">Credentials</AppLink> page.
      </Lead>

      <Callout kind="info">
        This guide is auto-generated from the integration registry. When a new integration is added to the registry, it appears here automatically — no separate documentation update needed.
      </Callout>

      {isPending || !data ? <P>Loading integrations…</P> : (
        (() => {
          const grouped = new Map<string, typeof data.integrations>();
          for (const i of data.integrations) {
            const arr = grouped.get(i.category) ?? [];
            arr.push(i);
            grouped.set(i.category, arr);
          }
          return (
            <>
              {Array.from(grouped.entries()).map(([cat, items]) => (
                <div key={cat}>
                  <H2>{CATEGORY_LABEL[cat] ?? cat}</H2>
                  <table className="my-2 w-full table-auto text-sm">
                    <thead className="text-2xs uppercase tracking-wider text-muted">
                      <tr>
                        <th className="px-2 py-1 text-left">Integration</th>
                        <th className="px-2 py-1 text-left">Required keys</th>
                        <th className="px-2 py-1 text-left">Status</th>
                        <th className="px-2 py-1 text-left">Setup</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {items.map((i) => (
                        <tr key={i.id}>
                          <td className="px-2 py-2">
                            <div className="font-medium text-ink">{i.name}</div>
                            <div className="text-2xs text-muted">{i.description}</div>
                          </td>
                          <td className="px-2 py-2 text-2xs font-mono text-muted-dark">
                            {i.fields.filter((f) => f.required).map((f) => f.key).join(", ") || "(none)"}
                          </td>
                          <td className="px-2 py-2">
                            {i.configured
                              ? <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-2xs font-medium text-emerald-700">✓ Configured</span>
                              : <span className="inline-flex items-center gap-1 rounded-full bg-line-soft px-2 py-0.5 text-2xs font-medium text-muted">Not configured</span>
                            }
                          </td>
                          <td className="px-2 py-2 text-2xs">
                            <a href={`#/credentials`} className="font-medium text-brand-700 hover:underline">
                              Open card →
                            </a>
                            {i.actions && i.actions.length > 0 && (
                              <div className="mt-0.5 text-2xs text-muted">
                                Actions: {i.actions.map((a) => a.label).join(", ")}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          );
        })()
      )}
    </>
  );
}

export const guide: Guide = { meta, Body };
